import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';

import { sentryConfig } from '../../config/sentryKey';
import { createBrowserHistory } from 'history';

// Error type tag for custom fingerprinting
const ERROR_TYPE_TAG = 'error.type';

// Business logic errors that should be logged as breadcrumbs, not errors
// These are expected user-facing errors, not bugs
const filteredErrors: { [key: string]: string } = {
  ERROR_LOCATION_UNAVAILABLE: 'Current location is unavailable. Make sure that location services are enabled',
  ERROR_LOCATION_DEVICE_SETTINGS: 'Location request failed due to unsatisfied device settings',
  ERROR_CHECKOUT_BEFORE_TIME: 'Checkout time before check-in time',
  ERROR_CHECKOUT_AFTER_SCHEDULED_TIME: 'Checkout time after scheduled time',
  ERROR_EARLY_CHECKIN: 'early for check-in the schedule',
  ERROR_LATE_CHECKIN: 'late for check-in the schedule',
  ERROR_SESSION_EXPIRED: 'Your session has expired. Please sign in again.',
  ERROR_FAILED_AUTH: 'Failed to authenticate',
  ERROR_CHECKIN_OUTSIDE_RADIUS: 'User Outside the Check-in Radius',
  ERROR_SCHEDULE_IS_NOT_DEFINED: 'Schedule is not defined',
  ERROR_DRAFT_PASSED_CHECKOUT_TIME: 'Continue the draft is not allowed as it already passed check-out time',
  ERROR_SCHEDULE_NOT_SELECTED_OR_EXIST: 'Schedule is not selected or does not exist',
  ERROR_AUTO_DATE_TIME: 'Unable to start report, Please enable auto date-time and auto time zone',
  ERROR_USER_NOT_FOUND: 'The user with the requested email or username was not found.',
  ERROR_USER_NOT_DELETED_FIREBASE: 'auth/user-not-found',
  ERROR_QUESTIONNAIRE_VALIDATION: 'Questionnaire validation failed',
  ERROR_FUTURE_DATE_CHECKIN: "Can't check in to the future date schedule",
  ERROR_CHECKIN_NOT_ALLOWED: 'Check-in is not allowed',
  ERROR_LATE_CHECKOUT: 'late for schedule check-out time',
  ERROR_INVALID_SCHEDULE_DATE: 'Invalid schedule date',
  ERROR_SITE_NOT_FOUND_OR_DISABLED: 'Site Not Found or Disabled',
  ERROR_REPORT_SUBMITTED: 'reportsubmitted',
  ERROR_NETWORK_CONNECTION: 'Network error: Please check your internet connection',
  ERROR_REQUEST_TIMEOUT: 'Request timed out: Please try again',
  ERROR_CONNECTION_FAILED: 'Unable to connect to server',
  ERROR_REQUEST_CANCELLED: 'Request was cancelled',
  ERROR_NETWORK_FAILED: 'Network connection failed',
  ERROR_CANCEL_ERROR: 'CANCEL_ERROR'
};

// Get environment-aware sample rates
const getSampleRates = (environment: string) => {
  switch (environment) {
    case 'production':
    case 'staging':
      return {
        tracesSampleRate: 1.0 // 100% sampling
      };
    case 'development':
    default:
      return {
        tracesSampleRate: 0.0 // No Sentry in dev
      };
  }
};

const getEnvironment = (nodeEnv: string | undefined): string => {
  const envMapping: { [key: string]: string } = {
    development: 'development',
    production: 'production',
    test: 'test'
  };
  return envMapping[nodeEnv || ''] || 'unknown';
};

// Get app version from package.json or env
const getAppVersion = (): string => {
  try {
    const pkg = require('../../../package.json');
    return process.env.REACT_APP_VERSION || pkg?.version || 'no-version';
  } catch {
    return 'no-version';
  }
};

const sentrySetup = () => {
  const envSentry = process.env.REACT_APP_SENTRY_ENVIRONMENT || getEnvironment(process.env.NODE_ENV);
  const history = createBrowserHistory();
  const sampleRates = getSampleRates(envSentry);

  // Skip Sentry initialization in development or if DSN is not configured
  if (envSentry === 'development' || !sentryConfig.dsn) {
    return;
  }

  Sentry.init({
    dsn: sentryConfig.dsn,
    environment: envSentry,
    release: getAppVersion(),
    integrations: [
      new Integrations.BrowserTracing({
        routingInstrumentation: Sentry.reactRouterV5Instrumentation(history)
      })
    ],
    tracesSampleRate: sampleRates.tracesSampleRate,
    maxBreadcrumbs: envSentry === 'production' ? 50 : 100,
    debug: false,
    attachStacktrace: true,
    autoSessionTracking: true,
    sendDefaultPii: true,
    normalizeDepth: 3,
    beforeSend(event, hint) {
      // Ensure appVersion tag is always present
      if (!event.tags?.appVersion) {
        event.tags = { ...event.tags, appVersion: getAppVersion() };
      }

      const error = hint.originalException as { message?: string };
      if (!error?.message) {
        return event;
      }

      const errorMessage = error.message;

      // Check if this is a business logic error that should be filtered
      const isFilteredError = Object.values(filteredErrors).some(filterMessage =>
        errorMessage?.toLowerCase().includes(filterMessage.toLowerCase())
      );

      if (isFilteredError) {
        // Log as breadcrumb instead of error event
        Sentry.addBreadcrumb({
          category: 'business_logic_error',
          message: 'Filtered business logic error',
          level: 'warning',
          data: {
            error_message: errorMessage,
            filtered: true,
            timestamp: new Date().toISOString()
          }
        });
        return null; // Don't send to Sentry as error
      }

      // Custom fingerprinting: Group errors by error.type tag
      const errorType = event.tags?.[ERROR_TYPE_TAG];
      if (errorType && typeof errorType === 'string') {
        event.fingerprint = [errorType];
      }

      return event;
    }
  });

  // Set app version tag
  Sentry.setTags({
    appVersion: getAppVersion()
  });
};

interface UserInfo {
  userID?: string;
  organizationID?: string;
  email?: string;
  role?: string;
  language?: string;
}

/**
 * Track user info for error context
 */
const trackUserInfo = (user: UserInfo) => {
  if (!user) return;

  // Set user identity (email kept here for user identification)
  Sentry.setUser({
    id: user.userID,
    email: user.email
  });

  // Set tags for filtering (excluding email to avoid PII in indexed tags)
  Sentry.setTags({
    userID: user.userID || '',
    organizationID: user.organizationID || '',
    role: user.role || ''
  });
};

/**
 * Clear user info on logout
 */
const clearUserInfo = () => {
  Sentry.setUser(null);
  Sentry.setTags({
    userID: undefined,
    organizationID: undefined,
    role: undefined
  });
};

/**
 * Log an error with context and custom error type for fingerprinting
 */
const logErrorWithContext = (error: Error | unknown, errorType: string, context?: Record<string, any>) => {
  if (process.env.NODE_ENV === 'development') {
    return;
  }

  Sentry.withScope(scope => {
    scope.setTag(ERROR_TYPE_TAG, errorType);
    scope.setContext('error_details', {
      error_type: errorType,
      ...context
    });

    Sentry.captureException(error, {
      level: 'error'
    });
  });
};

/**
 * Log an error without custom fingerprinting
 */
const logError = (error: Error | unknown, context?: Record<string, any>) => {
  if (process.env.NODE_ENV === 'development') {
    return;
  }

  Sentry.captureException(error, {
    level: 'error',
    contexts: {
      error_details: context || {}
    }
  });
};

/**
 * Add a breadcrumb for tracking user journey
 */
const addBreadcrumb = (
  category: string,
  message: string,
  level: 'info' | 'warning' | 'error' = 'info',
  data?: Record<string, any>
) => {
  if (process.env.NODE_ENV === 'development') {
    return;
  }

  Sentry.addBreadcrumb({
    category,
    message,
    level,
    data: data || {},
    type: 'default'
  });
};

/**
 * Track navigation/screen changes as breadcrumbs
 */
const trackNavigationChange = (previousRouteName: string | undefined, currentRouteName: string) => {
  if (process.env.NODE_ENV === 'development') {
    return;
  }

  if (previousRouteName !== currentRouteName) {
    Sentry.addBreadcrumb({
      category: 'navigation',
      message: `Navigated to ${currentRouteName}`,
      level: 'info',
      data: {
        from: previousRouteName || 'initial',
        to: currentRouteName
      }
    });
  }
};

/**
 * Track business operations (success/failure with metadata)
 */
const trackOperation = (operation: string, result: 'success' | 'failure', metadata?: Record<string, any>) => {
  if (process.env.NODE_ENV === 'development') {
    return;
  }

  Sentry.addBreadcrumb({
    category: 'operation',
    message: `${operation}: ${result}`,
    level: result === 'failure' ? 'warning' : 'info',
    data: {
      operation,
      result,
      timestamp: new Date().toISOString(),
      ...metadata
    }
  });
};

/**
 * Track performance for slow operations (>1000ms)
 */
const trackPerformance = (operation: string, metrics: { duration?: number; success?: boolean }) => {
  if (process.env.NODE_ENV === 'development') {
    return;
  }

  const { duration, success } = metrics;

  // Only track slow operations (>1000ms) as breadcrumbs
  if (duration && duration > 1000) {
    Sentry.addBreadcrumb({
      category: 'performance',
      message: `Slow operation: ${operation}`,
      level: 'warning',
      data: {
        operation,
        duration_ms: duration,
        success,
        timestamp: new Date().toISOString()
      }
    });
  }
};

/**
 * Wrap async operations with automatic timing
 */
const trackAsyncOperation = async <T>(
  name: string,
  fn: () => Promise<T>,
  metadata?: Record<string, any>
): Promise<T> => {
  const startTime = Date.now();

  try {
    const result = await fn();
    const duration = Date.now() - startTime;

    trackPerformance(name, { duration, success: true });
    trackOperation(name, 'success', { ...metadata, duration_ms: duration });

    return result;
  } catch (error) {
    const duration = Date.now() - startTime;

    trackPerformance(name, { duration, success: false });
    trackOperation(name, 'failure', {
      ...metadata,
      duration_ms: duration,
      error: error instanceof Error ? error.message : String(error)
    });

    throw error;
  }
};

/**
 * Track user actions (forms, buttons)
 */
const trackUserAction = (action: string, details?: Record<string, any>) => {
  if (process.env.NODE_ENV === 'development') {
    return;
  }

  Sentry.addBreadcrumb({
    category: 'user_action',
    message: action,
    level: 'info',
    data: {
      action,
      timestamp: new Date().toISOString(),
      ...details
    }
  });
};

/**
 * Generate a unique session ID for multi-step operations
 */
const generateSessionId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
};

/**
 * Track session start for multi-step operations
 */
const trackSessionStart = (sessionType: string, sessionId: string, metadata?: Record<string, any>) => {
  if (process.env.NODE_ENV === 'development') {
    return;
  }

  Sentry.addBreadcrumb({
    category: 'session',
    message: `Session started: ${sessionType}`,
    level: 'info',
    data: {
      session_type: sessionType,
      session_id: sessionId,
      event: 'start',
      timestamp: new Date().toISOString(),
      ...metadata
    }
  });
};

/**
 * Track session end for multi-step operations
 */
const trackSessionEnd = (
  sessionType: string,
  sessionId: string,
  result: 'success' | 'failure',
  metadata?: Record<string, any>
) => {
  if (process.env.NODE_ENV === 'development') {
    return;
  }

  Sentry.addBreadcrumb({
    category: 'session',
    message: `Session ended: ${sessionType} (${result})`,
    level: result === 'failure' ? 'warning' : 'info',
    data: {
      session_type: sessionType,
      session_id: sessionId,
      event: 'end',
      result,
      timestamp: new Date().toISOString(),
      ...metadata
    }
  });
};

export {
  sentrySetup,
  trackUserInfo,
  clearUserInfo,
  logErrorWithContext,
  logError,
  addBreadcrumb,
  trackNavigationChange,
  trackOperation,
  trackPerformance,
  trackAsyncOperation,
  trackUserAction,
  generateSessionId,
  trackSessionStart,
  trackSessionEnd
};
